import type { Address } from '../../types/addressBook';
import type { HazmatInfo, LabelRequestDto, OrderItem, Package } from '../../types/label';

export type LabelFormat = 'zpl' | 'png' | 'pdf';
export type WeightUnit = Package['weightType'];
export type DimensionUnit = Package['dimensionType'];
export type DestinationAddress = LabelRequestDto['destinationAddress'];
export type AddressType = DestinationAddress['addressType'];
export type AddressField = Exclude<keyof DestinationAddress, 'addressType'>;

export interface CustomsItem {
  customsValue: number;
  customsValueCurrency: string;
  commodityDescription: string;
  countryOfManufacture: string;
  quantity: number;
  productId?: string;
  quantityUnitOfMeasurement?: string;
  harmonizedTariffNumber?: string;
  commodityPartNumber?: string;
  marksAndNumbers?: string;
}

export interface CustomsInformation {
  totalCustomsValue?: number;
  totalCustomsValueCurrency?: string;
  customsDescription?: string;
  reasonForExport?: string;
  invoiceDate?: string;
  invoiceNumber?: string;
  ultimateConsigneeType?: string;
  aesInternalTransactionNumber?: string;
  incoterm?: string;
  customsItems?: CustomsItem[];
}

/** Draft rows carry a client-only `key` for stable React keys; it is stripped before submit. */
export interface ItemDraft extends OrderItem {
  key: string;
}

export interface PackageDraft {
  key: string;
  weight: string;
  weightUnit: WeightUnit;
  length: string;
  width: string;
  height: string;
  dimensionUnit: DimensionUnit;
  /** Prefilled PKG001, PKG002, ...; blank falls back to the positional default. */
  referenceId: string;
  partnerPackageId: string;
  /** Order items packed in this package. Always at least one. */
  items: ItemDraft[];
}

export interface CustomsItemDraft extends CustomsItem {
  key: string;
}

export interface LabelFormState {
  labelFormats: LabelFormat[];
  testMode: boolean;
  generateLabels: boolean;
  packages: PackageDraft[];
  address: DestinationAddress;
  /** User opted into customs for an address that does not require it. */
  customsOptIn: boolean;
  /** Undefined until a customs field is edited; display falls back to option defaults. */
  customs?: CustomsInformation;
  customsItems: CustomsItemDraft[];
}

export interface LabelCredentials {
  username: string;
  password: string;
  partnerId: string;
  partnerName: string;
}

export interface Option<T extends string = string> {
  value: T;
  label: string;
}

let keySeq = 0;
export const nextKey = (prefix: string) => `${prefix}-${++keySeq}`;

export const LABEL_FORMATS: ReadonlyArray<{ value: LabelFormat; label: string; description: string }> = [
  { value: 'zpl', label: 'ZPL', description: 'Thermal' },
  { value: 'png', label: 'PNG', description: 'Image' },
  { value: 'pdf', label: 'PDF', description: 'Print' },
];

export const WEIGHT_UNITS: ReadonlyArray<Option<WeightUnit>> = [
  { value: 'lb', label: 'lb' },
  { value: 'oz', label: 'oz' },
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
];

export const DIMENSION_UNITS: ReadonlyArray<Option<DimensionUnit>> = [
  { value: 'in', label: 'in' },
  { value: 'cm', label: 'cm' },
];

export const HAZMAT_CATEGORIES: readonly Option[] = [
  { value: 'contains_lithium_ion', label: 'Contains Lithium Ion' },
  { value: 'packaged_lithium_ion', label: 'Packaged Lithium Ion' },
  { value: 'lithium_ion_battery_only', label: 'Lithium Ion Battery Only' },
  { value: 'contains_lithium_metal', label: 'Contains Lithium Metal' },
  { value: 'packaged_lithium_metal', label: 'Packaged Lithium Metal' },
  { value: 'lithium_metal_battery_only', label: 'Lithium Metal Battery Only' },
  { value: 'dry_ice', label: 'Dry Ice' },
  { value: 'defined', label: 'Defined (Custom)' },
];

export const HAZMAT_QUANTITY_TYPES: readonly Option[] = [
  { value: 'gross', label: 'Gross' },
  { value: 'net', label: 'Net' },
];

export const HAZMAT_QUANTITY_UNITS: readonly Option[] = [
  { value: 'g', label: 'Grams (g)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'lb', label: 'Pounds (lb)' },
  { value: 'oz', label: 'Ounces (oz)' },
  { value: 'ml', label: 'Milliliters (ml)' },
  { value: 'l', label: 'Liters (l)' },
];

export const HAZMAT_CONTAINER_TYPES: readonly Option[] = [
  ['fiberboard_box', 'Fiberboard Box'],
  ['wooden_box', 'Wooden Box'],
  ['plastic_jerrican', 'Plastic Jerrican'],
  ['metal_box', 'Metal Box'],
  ['steel_drum', 'Steel Drum'],
  ['plastic_box', 'Plastic Box'],
  ['plastic_drum', 'Plastic Drum'],
  ['styrofoam_box', 'Styrofoam Box'],
  ['cylinder', 'Cylinder'],
  ['envirotainer', 'Envirotainer'],
  ['plywood_box', 'Plywood Box'],
  ['aluminum_drum', 'Aluminum Drum'],
  ['aluminum_cylinder', 'Aluminum Cylinder'],
  ['plastic_pail', 'Plastic Pail'],
  ['plywood_drum', 'Plywood Drum'],
  ['fiber_drum', 'Fiber Drum'],
  ['steel_jerrican', 'Steel Jerrican'],
  ['aluminum_jerrican', 'Aluminum Jerrican'],
  ['steel_box', 'Steel Box'],
  ['carton', 'Carton'],
  ['aluminum_box', 'Aluminum Box'],
  ['other', 'Other'],
].map(([value, label]) => ({ value, label }));

export const HAZMAT_PACKING_GROUPS: readonly Option[] = [
  { value: 'i', label: 'I' },
  { value: 'ii', label: 'II' },
  { value: 'iii', label: 'III' },
];

export const HAZMAT_TRANSPORT_MODES: readonly Option[] = [
  { value: 'ground', label: 'Ground' },
  { value: 'passenger_and_cargo_aircraft', label: 'Passenger and Cargo Aircraft' },
  { value: 'cargo_aircraft_only', label: 'Cargo Aircraft Only' },
];

export const HAZARD_CLASSES: readonly Option[] = [
  { value: 'class_1_explosive', label: 'Class 1 - Explosive' },
  { value: 'class_2_flammable_gas', label: 'Class 2 - Flammable Gas' },
  { value: 'class_3_flammable_liquid', label: 'Class 3 - Flammable Liquid' },
  { value: 'class_4_flammable_solid', label: 'Class 4 - Flammable Solid' },
  { value: 'class_5_organic_peroxide', label: 'Class 5 - Organic Peroxide' },
  { value: 'class_6_poisonous_material', label: 'Class 6 - Poisonous Material' },
  { value: 'class_7_radioactive', label: 'Class 7 - Radioactive' },
  { value: 'class_8_corrosive_material', label: 'Class 8 - Corrosive Material' },
  { value: 'class_9_miscellaneous', label: 'Class 9 - Miscellaneous' },
  { value: 'forbidden', label: 'Forbidden' },
  { value: 'other', label: 'Other' },
];

export const CURRENCIES: readonly Option[] = ['USD', 'EUR', 'GBP', 'CAD'].map((c) => ({ value: c, label: c }));

export const EXPORT_REASONS: readonly Option[] = [
  { value: 'sale', label: 'Sale' },
  { value: 'gift', label: 'Gift' },
  { value: 'sample', label: 'Sample' },
  { value: 'returns', label: 'Returns' },
  { value: 'personal_effects', label: 'Personal Effects' },
  { value: 'unknown', label: 'Unknown' },
];

export const CONSIGNEE_TYPES: readonly Option[] = [
  { value: 'direct_consumer', label: 'Direct Consumer' },
  { value: 'government_entity', label: 'Government Entity' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'unknown', label: 'Unknown' },
];

export const INCOTERMS: readonly Option[] = [
  { value: 'DDP', label: 'DDP - Delivered Duty Paid' },
  { value: 'DDU', label: 'DDU - Delivered Duty Unpaid' },
  { value: 'DAP', label: 'DAP - Delivered at Place' },
  { value: 'DPU', label: 'DPU - Delivered at Place Unloaded' },
  { value: 'FCA', label: 'FCA - Free Carrier' },
];

export const CUSTOMS_UNITS: readonly Option[] = ['piece', 'box', 'bag', 'roll', 'pallet'].map((u) => ({
  value: u,
  label: u[0].toUpperCase() + u.slice(1),
}));

/** Shown in selects when the field is untouched; matches what the backend defaults to. */
export const CUSTOMS_DISPLAY_DEFAULTS = {
  totalCustomsValueCurrency: 'USD',
  reasonForExport: 'sale',
  ultimateConsigneeType: 'direct_consumer',
  incoterm: 'DDP',
} as const;

export const DEFAULT_HAZMAT_INFO: HazmatInfo = {
  category: 'dry_ice',
  quantity: 1,
  quantityType: 'gross',
  quantityUnits: 'g',
  containerType: 'fiberboard_box',
};

export const LIMITED_QUANTITY = 'limited_quantity';

export const EMPTY_ADDRESS: DestinationAddress = {
  company: '',
  name: '',
  street1: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: 'US',
  addressType: 'residential',
};

export const newItem = (productId = 'PRODUCT001'): ItemDraft => ({
  key: nextKey('item'),
  productId,
  quantity: 1,
  productDetails: [],
  hazmat: false,
});

export const defaultPackageRef = (index: number) => `PKG${String(index + 1).padStart(3, '0')}`;

/** New package with one item; `from` supplies dimensions and units (not items or ids). */
export const newPackage = (referenceId = defaultPackageRef(0), from?: PackageDraft): PackageDraft => ({
  weight: '2',
  weightUnit: 'lb',
  length: '6',
  width: '4',
  height: '2',
  dimensionUnit: 'in',
  ...from,
  referenceId,
  partnerPackageId: '',
  items: [newItem()],
  key: nextKey('pkg'),
});

/** Deep copy of `pkg` (including its items) under a new reference id. */
export const copyPackage = (pkg: PackageDraft, referenceId: string): PackageDraft => ({
  ...newPackage(referenceId, pkg),
  items: pkg.items.map((item) => ({
    ...item,
    key: nextKey('item'),
    productDetails: [...(item.productDetails ?? [])],
    hazmatInfo: item.hazmatInfo && { ...item.hazmatInfo },
  })),
});

/** First PKG### not already used, starting after the current package count. */
export const nextPackageRef = (packages: readonly PackageDraft[]) => {
  const used = new Set(packages.map((p) => p.referenceId.trim()));
  let index = packages.length;
  while (used.has(defaultPackageRef(index))) index++;
  return defaultPackageRef(index);
};

export const allItems = (packages: readonly PackageDraft[]): ItemDraft[] => packages.flatMap((p) => p.items);

export const newCustomsItem = (): CustomsItemDraft => ({
  key: nextKey('customs'),
  customsValue: 0,
  customsValueCurrency: 'USD',
  commodityDescription: '',
  countryOfManufacture: 'US',
  quantity: 1,
  quantityUnitOfMeasurement: 'piece',
});

export const createInitialState = (): LabelFormState => ({
  labelFormats: ['zpl'],
  testMode: true,
  generateLabels: true,
  packages: [newPackage()],
  address: EMPTY_ADDRESS,
  customsOptIn: false,
  customs: undefined,
  customsItems: [newCustomsItem()],
});

const MILITARY_POSTAL = /^(APO|FPO|DPO)\s+(AE|AA|AP)\s+\d{5}$/i;

export const isInternational = (countryCode: string) => countryCode.trim() !== '' && countryCode !== 'US';

/**
 * Mirrors the backend's address classification: international and APO/FPO/DPO need customs.
 * A blank country is not treated as international while the user is still typing (it blocks submit anyway).
 */
export const isCustomsRequired = ({ countryCode, postalCode }: Pick<DestinationAddress, 'countryCode' | 'postalCode'>) =>
  isInternational(countryCode) || (countryCode === 'US' && MILITARY_POSTAL.test(postalCode));

export const isCustomsIncluded = (state: Pick<LabelFormState, 'address' | 'customsOptIn'>) =>
  state.customsOptIn || isCustomsRequired(state.address);

export const REQUIRED_ADDRESS_FIELDS: ReadonlyArray<[AddressField, string]> = [
  ['name', 'recipient name'],
  ['street1', 'street address'],
  ['city', 'city'],
  ['state', 'state'],
  ['postalCode', 'postal code'],
  ['countryCode', 'country'],
];

export const isBlank = (value: string | undefined) => !value || !value.trim();
export const isPositive = (value: string) => parseFloat(value) > 0;

/** Human-readable list of what blocks submission, most fundamental first. */
export const getMissingFields = (state: LabelFormState): string[] => {
  const missing: string[] = [];
  if (state.generateLabels && state.labelFormats.length === 0) missing.push('label format');
  if (state.packages.some((p) => !isPositive(p.weight))) missing.push('package weight');
  const items = allItems(state.packages);
  if (items.some((i) => isBlank(i.productId))) missing.push('product ID');
  if (items.some((i) => i.hazmat && i.hazmatInfo?.category === 'defined' && (isBlank(i.hazmatInfo.hazmatId) || isBlank(i.hazmatInfo.properShippingName)))) {
    missing.push('hazmat details');
  }
  for (const [field, label] of REQUIRED_ADDRESS_FIELDS) {
    if (isBlank(state.address[field])) missing.push(label);
  }
  if (isCustomsIncluded(state)) {
    if (isBlank(state.customs?.customsDescription)) missing.push('customs description');
    if (!(Number(state.customs?.totalCustomsValue) > 0)) missing.push('customs value');
    if (state.customsItems.some((i) => isBlank(i.commodityDescription))) missing.push('commodity description');
  }
  return missing;
};

const stripKey = <T extends { key: string }>({ key, ...rest }: T): Omit<T, 'key'> => rest;

const packageRef = (pkg: PackageDraft, index: number) => pkg.referenceId.trim() || defaultPackageRef(index);
const packageItems = (pkg: PackageDraft): OrderItem[] => pkg.items.map(stripKey);

/**
 * Builds the POST /label payload. Shape matches the pre-redesign page:
 * one package -> top-level dimensions + `orderItems`; several -> `packages` (multi-parcel),
 * each carrying its own `orderItems`.
 */
export const buildLabelRequest = (state: LabelFormState, credentials: LabelCredentials) => {
  const multiParcel = state.packages.length > 1;
  const [first] = state.packages;

  const customsInformation = isCustomsIncluded(state) && state.customs
    ? {
        ...state.customs,
        customsItems: state.customsItems
          .map(stripKey)
          .filter((item) => item.commodityDescription && item.countryOfManufacture && item.quantity > 0),
      }
    : undefined;

  const packages: Package[] = multiParcel
    ? state.packages.map((pkg, index) => ({
        packageReferenceIdentifier: packageRef(pkg, index),
        ...(pkg.partnerPackageId.trim() && { partnerProvidedPackageId: pkg.partnerPackageId.trim() }),
        packageWeight: pkg.weight,
        weightType: pkg.weightUnit,
        packageLength: pkg.length,
        packageWidth: pkg.width,
        packageHeight: pkg.height,
        dimensionType: pkg.dimensionUnit,
        orderItems: packageItems(pkg),
      }))
    : [];

  return {
    labelFormats: state.labelFormats,
    destinationAddress: state.address,
    packageWeight: first.weight,
    weightType: first.weightUnit,
    packageLength: first.length,
    packageWidth: first.width,
    packageHeight: first.height,
    dimensionType: first.dimensionUnit,
    testMode: state.testMode,
    generateLabels: state.generateLabels,
    customsInformation,
    orderItems: multiParcel ? [] : packageItems(first),
    packages,
    ...credentials,
  };
};

/** Address book entry -> form destination. */
export const fromAddressBook = (address: Address): DestinationAddress => ({
  company: address.company || '',
  name: address.recipientName,
  street1: address.street1,
  street2: address.street2 || '',
  city: address.city,
  state: address.state,
  postalCode: address.postalCode,
  countryCode: address.countryCode,
  phoneNumber: address.phoneNumber || '',
  emailAddress: address.emailAddress || '',
  addressType: address.addressType,
});

/** Shape the address book API expects when auto-saving a used destination. */
export const toAddressBookEntry = (address: DestinationAddress) => ({
  name: `Address - ${address.name}`,
  recipientName: address.name,
  street1: address.street1,
  street2: address.street2 || '',
  city: address.city,
  state: address.state,
  postalCode: address.postalCode,
  countryCode: address.countryCode,
  phoneNumber: address.phoneNumber || '',
  emailAddress: address.emailAddress || '',
  addressType: address.addressType,
});

export const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;
